import { ApiService } from '../ApiService';
import type {
  SupportedFormat,
  FormatDetectionResult,
  PreviewResult,
  ImportFromUrlRequest,
  ImportFromUrlResponse,
  ImportUploadResponse,
  ImportConfig,
  ImportTaskStatus,
  ValidationResult,
  ExportEntry,
  ExportConfig,
  ExportFiscalYearParams,
  ExportFecParams,
  TemplatesResponse
} from '../../../types/data-exchange';

const BASE = '/data-exchange';

export const dataExchangeApi = {
  // ====== FORMATS SUPPORTÉS ======

  /** Liste des formats d'import supportés */
  getImportFormats: () =>
    ApiService.get<{ formats: SupportedFormat[] }>(`${BASE}/formats/import`),

  /** Liste des formats d'export supportés */
  getExportFormats: () =>
    ApiService.get<{ formats: SupportedFormat[] }>(`${BASE}/formats/export`),

  // ====== DÉTECTION DE FORMAT ======

  /** Détection automatique du format d'un fichier uploadé */
  detectFormat: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = await getToken();
    const response = await fetch(`${getBaseUrl()}${BASE}/detect-format`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      return { success: false as const, error: err?.message || `HTTP ${response.status}` };
    }

    const raw = await response.json();
    // Dé-wrapper si le backend encapsule dans { data: {...} }
    const data = (raw?.data && raw?.data?.detectedFormats) ? raw.data : raw;
    return { success: true as const, data: data as FormatDetectionResult };
  },

  // ====== PRÉVISUALISATION ======

  /** Prévisualiser un fichier sans l'importer */
  preview: async (file: File, format?: string, maxLines?: number) => {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (format) params.append('format', format);
    if (maxLines) params.append('maxLines', String(maxLines));
    const qs = params.toString() ? `?${params.toString()}` : '';

    const token = await getToken();
    const response = await fetch(`${getBaseUrl()}${BASE}/preview${qs}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      return { success: false as const, error: err?.message || `HTTP ${response.status}` };
    }

    const data = await response.json();

    // Normaliser la réponse du backend pour le frontend
    const normalized = normalizePreviewResult(data);
    return { success: true as const, data: normalized };
  },

  // ====== IMPORT PAR UPLOAD (SYNCHRONE) ======

  /** Import direct par upload de fichier */
  importUpload: async (fiscalYearId: string, file: File, config: ImportConfig) => {
    const formData = new FormData();
    formData.append('file', file);
    // Champs de config en tant que champs FormData individuels (multipart/form-data)
    appendConfigToFormData(formData, config);

    // Construire les query params pour que le backend reçoive aussi le separator via URL
    const importParams = new URLSearchParams();
    if (config.separator) importParams.append('separator', config.separator);
    if (config.encoding) importParams.append('encoding', config.encoding);
    if (config.accountingStandard) importParams.append('format', config.accountingStandard);
    const importQs = importParams.toString() ? `?${importParams.toString()}` : '';

    console.log('📤 Import upload →', `${getBaseUrl()}${BASE}/import/${fiscalYearId}${importQs}`);
    console.log('📤 Config envoyée:', JSON.stringify(config, null, 2));
    console.log('📤 Fichier:', file.name, `(${(file.size / 1024).toFixed(1)} Ko)`);

    const token = await getToken();
    const response = await fetch(`${getBaseUrl()}${BASE}/import/${fiscalYearId}${importQs}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      console.error('❌ Import failed:', response.status, err);
      // Backend renvoie { success: false, error: { code, message, details } } ou { message: "..." }
      const errorMsg = err?.error?.message || err?.message || (Array.isArray(err?.message) ? err.message.join(', ') : null) || `HTTP ${response.status}`;
      return { success: false as const, error: errorMsg };
    }

    const raw = await response.json();
    console.log('✅ Import response:', JSON.stringify(raw, null, 2));
    // Dé-wrapper si le backend encapsule dans { success, data: {...} }
    const data = normalizeImportResponse(raw);
    return { success: true as const, data };
  },

  // ====== IMPORT DEPUIS URL (ASYNCHRONE) ======

  /** Démarrer un import asynchrone depuis une URL (Cloudinary/S3) */
  importFromUrl: (request: ImportFromUrlRequest) =>
    ApiService.post<ImportFromUrlResponse>(`${BASE}/import-from-url`, request),

  // ====== STATUT & GESTION DES TÂCHES ======

  /** Récupérer le statut d'une tâche d'import */
  getTaskStatus: (taskId: string) =>
    ApiService.get<ImportTaskStatus>(`${BASE}/import-task/${taskId}`),

  /** Lister toutes les tâches d'import */
  listTasks: () =>
    ApiService.get<ImportTaskStatus[]>(`${BASE}/import-tasks`),

  /** Annuler une tâche d'import */
  cancelTask: (taskId: string) =>
    ApiService.delete<{ success: boolean; message: string }>(`${BASE}/import-task/${taskId}`),

  // ====== VALIDATION (DRY-RUN) ======

  /** Valider un fichier sans l'importer */
  validate: async (file: File, config: ImportConfig) => {
    const formData = new FormData();
    formData.append('file', file);
    // Champs de config en tant que champs FormData individuels
    appendConfigToFormData(formData, config);

    const token = await getToken();
    const response = await fetch(`${getBaseUrl()}${BASE}/validate`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      console.error('❌ Validate failed:', response.status, err);
      const errorMsg = err?.error?.message || err?.message || (Array.isArray(err?.message) ? err.message.join(', ') : null) || `HTTP ${response.status}`;
      return { success: false as const, error: errorMsg };
    }

    const raw = await response.json();
    // Dé-wrapper si le backend encapsule dans { data: {...} }
    const data = (raw?.data && (raw.data.isValid !== undefined || raw.data.valid !== undefined)) ? raw.data : raw;
    return { success: true as const, data: data as ValidationResult };
  },

  // ====== EXPORT ======

  /** Exporter des écritures */
  exportEntries: async (entries: ExportEntry[], config: ExportConfig) => {
    const token = await getToken();
    const response = await fetch(`${getBaseUrl()}${BASE}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      },
      body: JSON.stringify({ entries, config })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      return { success: false as const, error: err?.message || `HTTP ${response.status}` };
    }

    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'export.txt';
    return { success: true as const, data: { blob, filename } };
  },

  /** Exporter un exercice fiscal complet */
  exportFiscalYear: async (fiscalYearId: string, params: ExportFiscalYearParams) => {
    const qs = new URLSearchParams();
    // Nouvelle API (prioritaire côté backend)
    qs.append('accountingStandard', params.accountingStandard);
    qs.append('fileFormat', params.fileFormat);
    qs.append('dataType', params.dataType);
    // Rétrocompatibilité ancienne API (format = accountingStandard)
    qs.append('format', params.accountingStandard);
    // Options
    if (params.startDate) qs.append('startDate', params.startDate);
    if (params.endDate) qs.append('endDate', params.endDate);
    if (params.encoding) qs.append('encoding', params.encoding);
    if (params.separator) qs.append('separator', params.separator);
    if (params.compress) qs.append('compress', 'true');
    if (params.currency) qs.append('currency', params.currency);
    if (params.accountingFramework) qs.append('accountingFramework', params.accountingFramework);

    const exportQs = qs.toString() ? `?${qs.toString()}` : '';
    const url = `${getBaseUrl()}${BASE}/export/fiscal-year/${fiscalYearId}${exportQs}`;
    console.log('📤 Export fiscal-year →', url);
    console.log('📤 Params:', JSON.stringify(params, null, 2));

    const token = await getToken();
    const response = await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      }
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      console.error('❌ Export fiscal-year failed:', response.status, err);
      return { success: false as const, error: err?.message || err?.error?.message || `HTTP ${response.status}` };
    }

    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `export_${fiscalYearId}.${params.fileFormat}`;
    console.log('✅ Export fiscal-year OK:', filename, `(${(blob.size / 1024).toFixed(1)} Ko)`);
    return { success: true as const, data: { blob, filename } };
  },

  /** Exporter un exercice au format FEC */
  exportFec: async (fiscalYearId: string, params: ExportFecParams) => {
    const qs = new URLSearchParams();
    qs.append('siren', params.siren);
    qs.append('closingDate', params.closingDate);

    const fecQs = qs.toString() ? `?${qs.toString()}` : '';
    const url = `${getBaseUrl()}${BASE}/export/fec/${fiscalYearId}${fecQs}`;
    console.log('📤 Export FEC →', url);

    const token = await getToken();
    const response = await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      }
    });

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      console.error('❌ Export FEC failed:', response.status, err);
      return { success: false as const, error: err?.message || err?.error?.message || `HTTP ${response.status}` };
    }

    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `${params.siren}FEC${params.closingDate.replace(/-/g, '')}.txt`;
    console.log('✅ Export FEC OK:', filename, `(${(blob.size / 1024).toFixed(1)} Ko)`);
    return { success: true as const, data: { blob, filename } };
  },

  // ====== TEMPLATES ======

  /** Templates CSV disponibles */
  getCsvTemplates: () =>
    ApiService.get<TemplatesResponse>(`${BASE}/templates/csv`),

  /** Templates SYSCOHADA disponibles */
  getSyscohadaTemplates: () =>
    ApiService.get<TemplatesResponse>(`${BASE}/templates/syscohada`),

  /** Télécharger un fichier exemple */
  downloadSampleTemplate: async (format: string) => {
    const token = await getToken();
    const response = await fetch(`${getBaseUrl()}${BASE}/templates/sample/${format}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
      }
    });

    if (!response.ok) {
      return { success: false as const, error: `HTTP ${response.status}` };
    }

    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `sample.${format}`;
    return { success: true as const, data: { blob, filename } };
  }
};

// ====== HELPERS INTERNES ======

/** Aplatit un objet ImportConfig en champs FormData individuels (multipart/form-data) */
function appendConfigToFormData(formData: FormData, config: ImportConfig): void {
  for (const [key, value] of Object.entries(config)) {
    if (value === undefined || value === null) continue;
    if (key === 'fiscalYearId') continue; // fiscalYearId est dans l'URL, pas dans le body
    if (typeof value === 'object') {
      // columnMapping ou autres objets → JSON stringifié
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  }
  // Log pour debug
  console.log('📋 FormData fields:');
  for (const [k, v] of formData.entries()) {
    if (k === 'file') {
      console.log(`  ${k}: [File]`);
    } else {
      console.log(`  ${k}: ${v}`);
    }
  }
}

/** Récupère le token d'auth via le provider global */
async function getToken(): Promise<string | null> {
  const { getAuthToken } = await import('../ApiService');
  return getAuthToken();
}

/** Base URL de l'API */
function getBaseUrl(): string {
  return import.meta.env.VITE_API_URL || 'http://localhost:8000/accounting/api/v1';
}

// ====== UTILITAIRE DE TÉLÉCHARGEMENT ======

/** Déclenche le téléchargement d'un blob dans le navigateur */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ====== NORMALISATION IMPORT RESPONSE ======

/**
 * Normalise la réponse brute du backend import.
 * Gère les deux cas :
 * - réponse directe : { importedCount, errorCount, ... }
 * - réponse encapsulée : { success: true, data: { totalRecords, successCount, ... } }
 * Et normalise les noms de champs (successCount → importedCount, totalRecords, etc.)
 */
function normalizeImportResponse(raw: any): ImportUploadResponse {
  // Dé-wrapper si encapsulé dans { data: {...} }
  const src = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
    ? raw.data
    : raw;

  return {
    success: src.success ?? raw.success ?? true,
    operationId: src.operationId ?? src.jobId ?? src.id ?? '',
    message: src.message ?? '',
    importedCount: src.importedCount ?? src.successCount ?? src.totalRecords ?? 0,
    skippedCount: src.skippedCount ?? 0,
    errorCount: src.errorCount ?? 0,
    fiscalYearId: src.fiscalYearId ?? '',
    errors: src.errors ?? [],
    warnings: src.warnings ?? [],
    duration: src.duration ?? 0
  };
}

// ====== NORMALISATION PREVIEW ======

/**
 * Normalise la réponse brute du backend pour correspondre au type PreviewResult
 * attendu par le frontend.
 *
 * Problèmes corrigés :
 * - backend peut encapsuler dans { data: {...} } ou retourner directement
 * - backend envoie `parsedLines` → frontend attend `totalLines`
 * - backend envoie `journalCounts: { code: count }` → frontend attend `journalCodes: string[]`
 * - backend n'envoie pas `isBalanced` → calculé ici
 * - backend n'envoie pas `dateRange` → extrait des données preview
 * - les lignes preview ont des clés normalisées (camelCase) mais les en-têtes sont les noms de colonnes du fichier source
 */
function normalizePreviewResult(raw: any): PreviewResult {
  // Dé-wrapper si le backend encapsule dans { data: {...} }
  const src = (raw?.data && raw?.data?.columnHeaders) ? raw.data : raw;
  const stats = src.statistics || {};

  // totalLines : le backend envoie parsedLines
  const totalLines = stats.totalLines ?? stats.parsedLines ?? src.totalPreviewLines ?? 0;

  // journalCodes : le backend envoie journalCounts (objet) pas journalCodes (array)
  const journalCodes: string[] = stats.journalCodes
    ?? (stats.journalCounts ? Object.keys(stats.journalCounts) : []);

  const totalDebit = stats.totalDebit ?? 0;
  const totalCredit = stats.totalCredit ?? 0;

  // isBalanced
  const isBalanced = stats.isBalanced ?? (Math.abs(totalDebit - totalCredit) < 0.01);

  // dateRange : tenter d'extraire des lignes preview
  let dateRange: { start: string; end: string } | null = stats.dateRange ?? null;
  if (!dateRange && Array.isArray(src.preview) && src.preview.length > 0) {
    const dates = src.preview
      .map((r: any) => r.entryDate || r.documentDate)
      .filter(Boolean)
      .map((d: string) => new Date(d).getTime())
      .filter((t: number) => !isNaN(t));
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      dateRange = {
        start: minDate.toLocaleDateString('fr-FR'),
        end: maxDate.toLocaleDateString('fr-FR')
      };
    }
  }

  return {
    success: src.success ?? true,
    detectedFormat: src.detectedFormat || '',
    columnHeaders: src.columnHeaders || [],
    preview: src.preview || [],
    statistics: {
      totalLines,
      parsedLines: stats.parsedLines,
      dateRange,
      journalCodes,
      journalCounts: stats.journalCounts,
      accountCounts: stats.accountCounts,
      totalDebit,
      totalCredit,
      isBalanced
    },
    totalPreviewLines: src.totalPreviewLines ?? (src.preview?.length ?? 0)
  };
}
